import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { v4 as uuidv4 } from 'uuid';
const BKJPillDropdownContext = createContext(null);
export const BKJPillDropdownProvider = ({ children, value = [], onChange, name, max, }) => {
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const mouseDownTargetRef = useRef(null);
    const [uuid] = useState(uuidv4());
    const [inputValue, setInputValue] = useState('');
    const [isFocused, setFocus] = useState(false);
    const onInputFocused = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        // When the input is already the active element (e.g., user just clicked
        // the input directly, or focus was programmatically moved here after a
        // chip remove), calling .focus() again is a no-op and does NOT fire a
        // fresh focus event — so Downshift's onFocus → openMenu does not run and
        // the dropdown stays closed even though the visual focus is correct.
        // Briefly blur first so the subsequent .focus() always fires a focus
        // event and re-opens the menu.
        if (document.activeElement === inputRef.current) {
            inputRef.current?.blur();
        }
        inputRef.current?.focus();
        setFocus(true);
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return void 0;
    }, []);
    const setNextValue = useCallback((nextValue) => {
        if (nextValue) {
            onChange({ target: { name, value: nextValue } });
            setInputValue('');
        }
        return void 0;
    }, [name, onChange]);
    const onInputChange = useCallback((e) => {
        setNextValue(e.target.value);
    }, [setNextValue]);
    const onChipRemove = useCallback((__VALUE__) => {
        return (event) => {
            event.preventDefault();
            event.stopPropagation();
            inputRef.current?.focus();
            setFocus(true);
            const index = value.indexOf(__VALUE__);
            const nextValue = Array.from(value).filter((value) => value !== 'select_all');
            nextValue.splice(index, 1);
            onChange({ target: { name, value: nextValue } });
        };
    }, [name, onChange, value]);
    const onInputBlur = useCallback((event) => {
        const containsTarget = event.currentTarget.contains(mouseDownTargetRef.current);
        if (containsTarget)
            event.stopPropagation();
        if (isFocused) {
            event.preventDefault();
            event.stopPropagation();
            setFocus(false);
        }
    }, [isFocused]);
    const isMaxLimitReached = useMemo(() => {
        return value.length > max;
    }, [max, value.length]);
    const onIsOpenChange = useCallback(({ isOpen }) => {
        if (!isOpen)
            setFocus(false);
    }, []);
    useEffect(() => {
        const inputEl = inputRef.current;
        if (!inputEl)
            return undefined;
        const handleFocus = () => setFocus(true);
        inputEl.addEventListener('focus', handleFocus);
        return () => {
            inputEl.removeEventListener('focus', handleFocus);
        };
    }, []);
    return (_jsx(BKJPillDropdownContext.Provider, { value: {
            wrapperRef,
            inputRef,
            value,
            inputValue,
            onInputFocused,
            onInputChange,
            onInputBlur,
            onChipRemove,
            isFocused,
            setInputValue,
            uuid,
            isMaxLimitReached,
            onIsOpenChange,
        }, children: children }));
};
export const useBKJPillDropdownContext = () => useContext(BKJPillDropdownContext);
//# sourceMappingURL=BKJPillDropdown.context.js.map